import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import GlobalDrawer from 'components/Drawer';
import Input from 'components/Input';
import NewTextArea from 'components/TextArea/NewTextArea';

interface Faculty {
  name: string;
  designation: string;
  yearsOfExperience: string;
  gender: string;
  mobileNumber: string;
  emailAddress: string;
  subject: string;
  description: string;
}

const NewFaculties: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [facultyMembers, setFacultyMembers] = useState<Faculty[]>([]);
  const [editingFacultyIndex, setEditingFacultyIndex] = useState<number | null>(null);

  // Opens the drawer for adding a new faculty
  const handleOpenDrawer = () => {
    setEditingFacultyIndex(null);
    setDrawerOpen(true);
  };

  // Closes the drawer and resets edit mode
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingFacultyIndex(null);
  };

  // Opens the drawer for editing an existing faculty card
  const handleEditFaculty = (index: number) => {
    setEditingFacultyIndex(index);
    setDrawerOpen(true);
  };

  // Deletes a faculty card
  const handleDeleteFaculty = (index: number) => {
    const updatedFaculty = [...facultyMembers];
    updatedFaculty.splice(index, 1);
    setFacultyMembers(updatedFaculty);
  };

  // Validation schema for the form using Yup
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    designation: Yup.string().required('Designation is required'),
    yearsOfExperience: Yup.string().required('Years of experience is required'),
    gender: Yup.string().required('Gender is required'),
    mobileNumber: Yup.string().required('Mobile number is required'),
    emailAddress: Yup.string().email('Invalid email').required('Email is required'),
    subject: Yup.string().required('Subject is required')
  });

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#4a5568',
    marginBottom: '0.25rem'
  };

  const textareaLabel={
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#4a5568',
  }

  // Render a single faculty card
  const renderFacultyCard = (faculty: Faculty, index: number) => {
    return (
      <div
        key={index}
        className="bg-white rounded-lg shadow-lg p-3 flex flex-col"
        style={{ fontFamily: "'Manrope', sans-serif", width: '100%', height: '100%' }}
      >
        {/* Header with avatar, name, and action buttons */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gray-200 rounded-full mr-2 mt-1 flex items-center justify-center">
              <span className="text-gray-500 font-medium">
                {faculty.name ? faculty.name.charAt(0) : ''}
              </span>
            </div>
            <div className="flex-1">
              <p
                className="font-medium text-gray-900 text-sm truncate"
                title={faculty.name}
              >
                {faculty.name}
              </p>
              <p className="text-gray-500 text-xs">{faculty.yearsOfExperience} years | {faculty.gender === 'male' ? 'Male' : faculty.gender === 'female' ? 'Female' : 'Other'}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleEditFaculty(index)}
              className="text-gray-500 hover:text-blue-600"
            >
              <img src="/images/edit-3.svg" alt="edit" className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteFaculty(index)}
              className="text-gray-500 hover:text-red-600"
            >
              <img src="/images/trash.svg" alt="delete" className="w-4 h-4" />
            </button>
          </div>
        </div>

        <hr className="w-full my-1 border-gray-200" />

        {/* Details section */}
        <div className="space-y-0.5 text-xs mt-1 flex-grow">
          <div className="flex justify-between items-center mt-1">
            <span className="text-gray-500 leading-relaxed">Designation</span>
            <span
              className="font-medium text-gray-800 truncate"
              style={{ maxWidth: "10ch" }}
              title={faculty.designation}
            >
              {faculty.designation}
            </span>
          </div>
          <div className="flex justify-between items-center mt-1 ">
            <span className="text-gray-500 leading-relaxed">Subject</span>
            <span
              className="font-medium text-gray-800 truncate"
              style={{ maxWidth: "10ch" }}
              title={faculty.subject}
            >
              {faculty.subject}
            </span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-gray-500 leading-relaxed">Experience</span>
            <span className="font-medium text-gray-800">{faculty.yearsOfExperience}</span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-gray-500 leading-relaxed">Mobile Number</span>
            <span
              className="font-medium text-gray-800 truncate"
              style={{ maxWidth: "10ch" }}
              title={faculty.mobileNumber}
            >
              {faculty.mobileNumber}
            </span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-gray-500 leading-relaxed">Email Address</span>
            <span
              className="font-medium text-gray-800 truncate"
              style={{ maxWidth: "10ch" }}
              title={faculty.emailAddress}
            >
              {faculty.emailAddress}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Determine the initial form values.
  // If editingFacultyIndex is not null, use that faculty's data; otherwise, use empty fields.
  const initialFormValues: Faculty =
    editingFacultyIndex !== null
      ? facultyMembers[editingFacultyIndex]
      : {
        name: '',
        designation: '',
        yearsOfExperience: '',
        gender: '',
        mobileNumber: '',
        emailAddress: '',
        subject: '',
        description: ''
      };

  return (
    <div className="min-h-screen font-manrope p-7">
      <h2 className="text-2xl font-semibold mb-1">Let's Show Our Heroes!</h2>
      <p className="text-md text-gray-500 mb-4">
        Upload your teachers' photos and showcase the inspiring faces behind the
        learning. Highlight the educators shaping your students' future!
      </p>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-medium mb-1">Add Faculty Profiles</h3>
          <p className="text-sm text-gray-500">Maximum 5 Profile</p>
        </div>

        {/* Add Faculty Member button aligned with the heading text */}
        {facultyMembers.length > 0 && facultyMembers.length < 5 && (
          <button
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            onClick={handleOpenDrawer}
            style={{ color: 'white' }}
          >
            Add Faculty Member
          </button>
        )}
      </div>

      {/* Render grid of cards if faculty exist; otherwise, show the default add card */}
      {facultyMembers.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {facultyMembers.map((faculty, index) => (
            <div key={index} className="w-64 h-48 flex-shrink-0 m-3">
              {renderFacultyCard(faculty, index)}
            </div>
          ))}
        </div>
      ) : (
        // Default card when there are no faculty members yet
        <div className="flex justify-center items-center">
          <div className="w-96 h-48 bg-white rounded-lg shadow-sm border border-gray-100 flex flex-col items-center justify-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <img src="/images/UserPlus.svg" alt="User Plus" className="w-8 h-8" />
            </div>
            <div className="text-center">
              <p className="text-base font-medium mb-1">Add Faculty Details</p>
              <p className="text-xs text-gray-500">Maximum 5 Profile</p>
            </div>
            <button
              className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              onClick={handleOpenDrawer}
              style={{ color: 'white' }}
            >
              Add Faculty Member
            </button>
          </div>
        </div>
      )}

      {/* Faculty Details Drawer */}
      <GlobalDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        drawerHeading={
          editingFacultyIndex !== null ? 'Edit Faculty Details' : 'Add Faculty Details'
        }
       width={650}
        placement="right"
      >
        <Formik
          enableReinitialize
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            if (editingFacultyIndex !== null) {
              // Edit mode: update the specific faculty member
              const updatedFaculty = [...facultyMembers];
              updatedFaculty[editingFacultyIndex] = values;
              setFacultyMembers(updatedFaculty);
            } else {
              // Add new faculty member
              setFacultyMembers([...facultyMembers, values]);
            }
            setSubmitting(false);
            resetForm();
            handleCloseDrawer();
          }}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeHolder="Enter name"
                  label="Name"
                  customHeight="30px"
                  labelStyle={labelStyle}
                  customStyle={{ borderColor: '#e2e8f0' }} 
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Designation
                  </label>
                  <Field
                    as="select"
                    name="designation"
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select designation</option>
                    <option value="Principal">Principal</option>
                    <option value="Vice Principal">Vice Principal</option>
                    <option value="Head of Department">Head of Department</option>
                    <option value="Professor">Professor</option>
                    <option value="Assistant Professor">Assistant Professor</option>
                    <option value="Director">Director</option>
                  </Field>
                  <ErrorMessage
                    name="designation"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    name="yearsOfExperience"
                    labelStyle={labelStyle}
                    placeHolder="Enter years of experience"
                    label="Years Of Experience"
                  />
                  <ErrorMessage
                    name="yearsOfExperience"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <Field
                    as="select"
                    name="gender"
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Field>
                  <ErrorMessage
                    name="gender"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <Field
                    as="select"
                    name="subject"
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select subject</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Science">Science</option>
                    <option value="English">English</option>
                    <option value="Computer Science">Computer Science</option>
                    <option value="History">History</option>
                  </Field>
                  <ErrorMessage
                    name="subject"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    name="mobileNumber"
                    placeHolder="Enter mobile number"
                    labelStyle={labelStyle}
                    label="Mobile Number"
                  />
                  <ErrorMessage
                    name="mobileNumber"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="emailAddress"
                    placeHolder="Enter email address"
                    labelStyle={labelStyle}
                    label="Email Address"
                  />
                  <ErrorMessage
                    name="emailAddress"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Faculty Profile Picture
                </label>
                <div className="flex justify-between items-center p-2 border border-gray-300 rounded-md">
                  <span className="text-sm text-gray-500">
                    Upload Faculty Profile Picture
                  </span>
                  <button
                    type="button"
                    className="py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-300 text-sm font-medium"
                    style={{ color: 'white' }}
                  >
                    Upload
                  </button>
                </div>
              </div>

              <div className='relative w-full'>
                <NewTextArea
                  label="Description"
                  name="description"
                  autoGenerate={true}
                  required={true}
                  labelStyle={textareaLabel}
                  // placeHolder="Fill above details and our AI will generate content for you"
                // You can pass a 'generate' prop if your API requires additional configuration
                />
              </div>

              <div className="flex justify-end pt-4 space-x-3">
                <button
                  type="button"
                  onClick={handleCloseDrawer}
                  className="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none hover:bg-gray-100"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-300 focus:outline-none"
                  style={{ color: 'white' }}
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </GlobalDrawer>
    </div>
  );
};

export default NewFaculties;
